/**
 * Single hook that resolves initial wizard values for all three pre-fill paths:
 *
 *   1. Adhoc  — no API. Writes ADHOC_PAYLOAD_DEFAULTS into payloadRef.
 *   2. Create — GET /ats_interviews/new. Writes hydratePlanDefaults into payloadRef.
 *   3. Edit   — GET /ats_interviews/:id or /ats_schedules/:id.
 *               Writes hydratePayloadFromApi into payloadRef.
 *
 * payloadRef is fully hydrated for ALL steps before any step form mounts,
 * making create and edit paths symmetric.
 *
 * Replaces: useEditInterviewFetch + useEditPrefill + useInterviewPlanDefaults.
 */
import { useEffect, useMemo, useRef } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useGetInterview } from '@/api/useInterviews';
import { useGetSelfSchedule } from '@/api/useSelfSchedule';
import { useShowToast } from '@/hooks/useShowToast';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import type {
  ScheduleMode,
  ApiReference,
  ScheduleInterviewPayload,
} from '../config/types';
import { isAdhocMode, isSelfScheduleMode } from '../config/modeClassifiers';
import { useInterviewPlan } from '../api/useInterviewPlan';
import { ADHOC_PAYLOAD_DEFAULTS } from './uiDefaults';
import { hydratePlanDefaults } from './fromPlan';
import { hydratePayloadFromApi } from './fromEditApi';

interface UseWizardInitialValuesOptions {
  mode: ScheduleMode;
  apiReference: ApiReference;
  stageId?: number;
  stageActivityId?: number;
  candidateId?: number;
  interviewId?: number;
  scheduleId?: number;
}

/** Zoom-status shape lifted off the plan response so adhoc create can paint
 *  the Host (Zoom) chip without exposing the rest of `interviewPlan` to
 *  adhoc consumers (which assume it's `undefined`). */
export interface CurrentUserZoomStatus {
  can_host_zoom_meeting?: boolean;
  zoom_rate_limit_reached?: boolean;
  zoom_plan?: number;
  zoom_plan_type?: string;
}

interface UseWizardInitialValuesResult {
  payloadRef: React.MutableRefObject<ScheduleInterviewPayload>;
  prefillVersion: number;
  /** Raw API response kept as a prop for behavioral flags
   *  (shared_calendar_enabled, minimum_notice_config, zoom_status, etc.).
   *  Initial VALUES come from payloadRef — not from this object.
   *  Always `undefined` for adhoc — consumers rely on this. Adhoc surfaces
   *  the current user's zoom status separately via `currentUserZoomStatus`. */
  interviewPlan: Record<string, unknown> | undefined;
  /** Adhoc-only: current user's zoom status pulled from the plan response
   *  the application's stage points to. Mirrors legacy `SchZoomHost` which
   *  always had the plan API available (legacy adhoc passed
   *  `scheduleType="manual"` so the plan fetch ran). Undefined for
   *  non-adhoc flows (those read zoom status off `interviewPlan` directly). */
  currentUserZoomStatus?: CurrentUserZoomStatus;
  /** Adhoc-only: plan-level zoom host strategy ('organizer' vs 'interviewer')
   *  pulled from the same plan response. Drives `pickAutoHostId` priority —
   *  legacy adhoc respected this account setting; the new wizard's adhoc was
   *  dropping it on the floor and always defaulting to interviewer-first. */
  planZoomHostType?: 'organizer' | 'interviewer' | string | null;
  showCreateLoader: boolean;
  showEditLoader: boolean;
  showEditError: boolean;
}

export function useWizardInitialValues({
  mode,
  apiReference,
  stageId,
  stageActivityId,
  candidateId,
  interviewId,
  scheduleId,
}: UseWizardInitialValuesOptions): UseWizardInitialValuesResult {
  const isEdit = apiReference === 'edit';
  const isAdhoc = isAdhocMode(mode);
  const isSelfSchedule = isSelfScheduleMode(mode);

  // When the account/job setting marks AI notetaker as mandatory:
  //   - seed must always be `true` regardless of plan/existing value
  //   - the field is rendered as disabled so users can't turn it off
  //   (field disable is in useNoteTakerConfig; seeding is done below)
  const { currentJob = {} } = useSelector(getAtsJobCreate, shallowEqual);
  const isNoteTakerMandatory = Boolean(
    (currentJob as Record<string, unknown>)?.data?.note_taker?.mandatory
  );

  const payloadRef = useRef<ScheduleInterviewPayload>({
    details: {},
    communication: {},
    scorecard: {},
  });

  // ── Path 1: Adhoc ──────────────────────────────────────────────────
  // No API call. Seed payloadRef synchronously on first render so the
  // form mounts with the correct blank defaults.
  const adhocInitRef = useRef(false);
  if (isAdhoc && !adhocInitRef.current) {
    adhocInitRef.current = true;
    payloadRef.current = {
      ...ADHOC_PAYLOAD_DEFAULTS,
      details: { ...ADHOC_PAYLOAD_DEFAULTS.details },
    };
    if (isNoteTakerMandatory) {
      payloadRef.current.details.enable_note_taker = true;
    }
  }
  if (!isAdhoc) {
    adhocInitRef.current = false;
  }

  // ── Path 3: Edit — fetch ────────────────────────────────────────────
  const useScheduleGet = isEdit && isSelfSchedule && !!scheduleId;
  const toast = useShowToast();

  const {
    data: interviewFromInterviewsEndpoint,
    isLoading: isLoadingInterview,
    isError: isInterviewLoadError,
    isFetched: isFetchedInterview,
  } = useGetInterview(interviewId, {
    enabled: isEdit && !useScheduleGet && !!interviewId,
  });

  const {
    data: interviewFromSchedulesEndpoint,
    isLoading: isLoadingSchedule,
    isError: isScheduleLoadError,
    isFetched: isFetchedSchedule,
  } = useGetSelfSchedule(scheduleId, {
    enabled: useScheduleGet,
  });

  const existingInterview = useScheduleGet
    ? interviewFromSchedulesEndpoint
    : interviewFromInterviewsEndpoint;
  const isLoadingExisting = useScheduleGet
    ? isLoadingSchedule
    : isLoadingInterview;
  const isLoadError = useScheduleGet
    ? isScheduleLoadError
    : isInterviewLoadError;
  const isFetchedExisting = useScheduleGet
    ? isFetchedSchedule
    : isFetchedInterview;

  useEffect(() => {
    if (!isLoadError || !isEdit) return;
    toast({
      title: 'Could not load interview',
      description: 'Try closing and opening the editor again.',
      status: 'error',
    });
  }, [isEdit, isLoadError, toast]);

  // ── Path 3: Edit — hydrate payloadRef ──────────────────────────────
  const lastHydratedIdRef = useRef<number | null>(null);
  const prefillGenerationRef = useRef(0);

  if (!isEdit) {
    lastHydratedIdRef.current = null;
  }

  const editDataReady =
    isEdit &&
    existingInterview !== undefined &&
    isFetchedExisting &&
    !isLoadingExisting;

  if (editDataReady && existingInterview.id !== lastHydratedIdRef.current) {
    lastHydratedIdRef.current = existingInterview.id;
    prefillGenerationRef.current += 1;
    payloadRef.current = hydratePayloadFromApi(existingInterview);
    if (isNoteTakerMandatory) {
      payloadRef.current.details.enable_note_taker = true;
    }
  }

  // ── Path 2: Create — fetch plan ────────────────────────────────────
  // Fetch whenever a stageId is available — including adhoc. Legacy parity:
  // legacy adhoc routed through `scheduleType="manual"` + `isAdhoc={true}`
  // so the plan query always ran (gate was on schedule type, not isAdhoc).
  // The plan response carries the current user's Zoom status; adhoc needs
  // it to paint the Host chip + auto-pick the recruiter as host.
  //
  // Adhoc still owns its own defaults — `hydratePlanDefaults` and the
  // `interviewPlan` return value remain gated by `!isAdhoc` below so no
  // plan-derived behavior leaks into adhoc forms. Only the Zoom-status
  // slice is surfaced (via `currentUserZoomStatus`).
  const planEnabled = !isEdit && !!stageId && stageId > 0;

  const { data: planData, isLoading: isLoadingPlan } = useInterviewPlan(
    {
      stageId: stageId ?? 0,
      stageActivityId: stageActivityId ?? undefined,
      contactId: candidateId ?? undefined,
    },
    { enabled: planEnabled }
  );

  // Hydrate payloadRef once when plan resolves so ALL steps have their
  // defaults before any form mounts (gated by showCreateLoader).
  const planHydratedRef = useRef(false);
  if (!isEdit) planHydratedRef.current = false;
  if (!isEdit && !isAdhoc && planData && !planHydratedRef.current) {
    planHydratedRef.current = true;
    payloadRef.current = hydratePlanDefaults(planData, mode);
    if (isNoteTakerMandatory) {
      payloadRef.current.details.enable_note_taker = true;
    }
  }

  // ── Derive interviewPlan for behavioral flags ──────────────────────
  // Edit: use the raw existing interview (feature flags live there).
  // Create: use the raw plan response.
  // Adhoc: undefined — downstream consumers branch on this; flipping to
  // truthy would silently activate plan-derived behavior in adhoc
  // (shared_calendar_enabled, meeting_rooms_enabled, minimum_notice_*,
  // zoom_host_type, calendar_info, comm-template prefill, etc.).
  const interviewPlan: Record<string, unknown> | undefined = isEdit
    ? (existingInterview as Record<string, unknown> | undefined)
    : isAdhoc
      ? undefined
      : planData;

  // ── Derive current user's Zoom status (adhoc only) ─────────────────
  // The plan response emits zoom data in two shapes — nested under
  // `user_zoom_status` (typically edit / specific create paths) and as
  // flat root-level fields (the `_zoom_status.json.jbuilder` partial at
  // the root of `ats_interviews/new`). DetailsStep already handles both
  // shapes off `interviewPlan` for non-adhoc; here we surface only the
  // current-user slice so adhoc gets the chip without re-exposing the
  // rest of the plan.
  const currentUserZoomStatus = useMemo<
    CurrentUserZoomStatus | undefined
  >(() => {
    if (!isAdhoc || !planData) return undefined;
    const root = planData as Record<string, unknown>;
    const nested = root.user_zoom_status as
      | Record<string, unknown>
      | null
      | undefined;
    if (nested && typeof nested === 'object') {
      return {
        can_host_zoom_meeting: nested.can_host_zoom_meeting as
          | boolean
          | undefined,
        zoom_rate_limit_reached: nested.zoom_rate_limit_reached as
          | boolean
          | undefined,
        zoom_plan: nested.zoom_plan as number | undefined,
        zoom_plan_type: nested.zoom_plan_type as string | undefined,
      };
    }
    if (
      'can_host_zoom_meeting' in root ||
      'zoom_plan' in root ||
      'zoom_rate_limit_reached' in root
    ) {
      return {
        can_host_zoom_meeting: root.can_host_zoom_meeting as
          | boolean
          | undefined,
        zoom_rate_limit_reached: root.zoom_rate_limit_reached as
          | boolean
          | undefined,
        zoom_plan: root.zoom_plan as number | undefined,
        zoom_plan_type: root.zoom_plan_type as string | undefined,
      };
    }
    return undefined;
  }, [isAdhoc, planData]);

  // Plan-level Zoom host strategy. Same gating + sourcing as
  // `currentUserZoomStatus` — only surfaced in adhoc so non-adhoc consumers
  // keep reading from `interviewPlan?.zoom_host_type` unchanged.
  const planZoomHostType = useMemo<
    'organizer' | 'interviewer' | string | null | undefined
  >(() => {
    if (!isAdhoc || !planData) return undefined;
    const raw = (planData as Record<string, unknown>).zoom_host_type;
    if (raw === null) return null;
    if (typeof raw === 'string') return raw;
    return undefined;
  }, [isAdhoc, planData]);

  return {
    payloadRef,
    prefillVersion: isEdit ? prefillGenerationRef.current : 0,
    interviewPlan,
    currentUserZoomStatus,
    planZoomHostType,
    // Adhoc mounts instantly with ADHOC_PAYLOAD_DEFAULTS — never gate the
    // form on the plan fetch landing. The plan fetch is fire-and-forget
    // in adhoc; the chip simply paints when it arrives.
    showCreateLoader: planEnabled && !isAdhoc && isLoadingPlan,
    showEditLoader: isEdit && isLoadingExisting,
    showEditError: isEdit && isLoadError,
  };
}
